/**
 * Summary tab — one-liner, presentation, admission context, and fallback problems list.
 */
import { FC } from 'react'
import ReactMarkdown from 'react-markdown'
import type { PatientPanelData } from '../types'
import { PanelSkeleton } from '../shared/PanelSkeleton'
import { ProblemsList } from '../../chat/patient-panel/ProblemsList'

export const SummaryTab: FC<{ data: PatientPanelData }> = ({ data }) => {
  // Loading state
  if (data.isGenerating && !data.summary) {
    return (
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
        <PanelSkeleton lines={6} />
      </div>
    )
  }

  // Fallback: extractions exist but no parser summary
  const hasExtractions = data.meta?.extractions && data.meta.extractions.length > 0
  const problemExtractions = data.meta?.extractions?.filter(e => e.category === 'problem') ?? []

  if (!data.summary && hasExtractions && problemExtractions.length > 0) {
    return (
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
        <ProblemsList
          extractions={problemExtractions}
          patientId="summary-fallback"
        />
      </div>
    )
  }

  // Empty state
  if (!data.summary) {
    return (
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3 flex items-center justify-center">
        <p className="text-xs text-muted-foreground opacity-40">
          Upload chart data to generate patient summary
        </p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-3">
      {/* One-liner card */}
      {data.summary.oneLiner && (
        <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-3">
          <p className="text-sm font-semibold text-clinical-teal leading-snug">
            {data.summary.oneLiner}
          </p>
        </div>
      )}

      {/* Presentation */}
      {data.summary.presentation && (
        <div>
          <h4 className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
            Presentation
          </h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {data.summary.presentation}
          </p>
        </div>
      )}

      {/* Admission Context */}
      {data.summary.admissionContext && (
        <div>
          <h4 className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
            Admission Context
          </h4>
          <div className="text-xs text-muted-foreground leading-relaxed prose prose-xs prose-invert max-w-none">
            <ReactMarkdown>{data.summary.admissionContext}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  )
}
